/* assets/ba-support-product.js
   WHAT: Sticky topic rail behaviour for the per-product support page.
         (a) highlights the rail link for whichever topic you are looking at,
         (b) opens the matching accordion when a rail link or a deep link is used.

   WHY:  Owner 2026-08-24 — "the buttons for the subjects these need to stay sticky
         on the page so that they can navigate around the page... they should be
         highlighted when it is selected."

   The highlight is DERIVED from scroll position and the URL hash, never written into
   the markup (build rule 24 — no hardcoded is-active). Position is recomputed rather
   than cached because the accordions are <details>: opening one changes every
   subsequent offset, so a cached list would highlight the wrong topic.

   WHEN: 2026-08-24.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-support-rail-root]');
  if (!root) return;

  var links = [].slice.call(root.querySelectorAll('[data-rail-target]'));
  if (!links.length) return;

  // Offset for the sticky site header, so a topic counts as "current" once its
  // heading reaches just under the header rather than the very top of the viewport.
  var HEADER_OFFSET = 120;

  function panelFor(link) {
    return document.getElementById(link.getAttribute('data-rail-target'));
  }

  function setActive(link) {
    links.forEach(function (l) {
      var on = l === link;
      l.classList.toggle('is-active', on);
      // aria-current tells a screen reader which topic is in view, matching the visual
      if (on) { l.setAttribute('aria-current', 'location'); }
      else { l.removeAttribute('aria-current'); }
    });
  }

  function currentLink() {
    var best = null;
    var y = window.pageYOffset + HEADER_OFFSET + 1;

    for (var i = 0; i < links.length; i++) {
      var panel = panelFor(links[i]);
      if (!panel) continue;
      // Read live each pass — an opened <details> shifts everything below it.
      if (panel.getBoundingClientRect().top + window.pageYOffset <= y) { best = links[i]; }
    }

    // Above the first topic, nothing is current yet; at the very bottom the last
    // topic is, even if its heading has scrolled past.
    var atBottom = (window.innerHeight + window.pageYOffset) >= (document.body.scrollHeight - 4);
    if (atBottom) { best = links[links.length - 1]; }

    return best;
  }

  // Set when a URL hash chose the topic. Opening that panel fires a `toggle`, which
  // would otherwise recompute the highlight from a scroll position that has not moved
  // yet and clear it. Only a real scroll releases the lock — and by the time that
  // fires, the position is at the target, so the recomputed answer is the right one.
  var hashLock = false;

  var queued = false;
  function onScroll() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      if (hashLock) return;
      var link = currentLink();
      if (link) { setActive(link); }
      else { setActive(null); }
    });
  }

  // Opening the panel a link points at — used by rail clicks and by deep links/QR.
  function openTarget(id) {
    if (!id) return null;
    var el = document.getElementById(id);
    if (el && el.tagName === 'DETAILS') { el.open = true; }
    return el;
  }

  links.forEach(function (link) {
    link.addEventListener('click', function (e) {
      var id = link.getAttribute('data-rail-target');
      var el = openTarget(id);
      setActive(link);
      hashLock = true;   // the click's own toggle must not undo the highlight

      // Owner 2026-08-25: "if i click on faq and then back it is still not the same
      // page, they should be anchors and the back should go back to the categories
      // page."
      //
      // A plain #anchor jump PUSHES a history entry, so after reading three topics
      // the reader has to press Back three times just to leave the page. These are
      // in-page moves, not navigations - they should not consume the back button.
      // So the jump is handled here and the hash is written with replaceState:
      // the URL still updates and stays shareable, but Back returns to wherever the
      // reader came from, which is the support categories page.
      if (!el || !history.replaceState) return;   // no JS support: let the browser do it
      e.preventDefault();
      if (typeof el.scrollIntoView === 'function') { el.scrollIntoView({ block: 'start' }); }
      history.replaceState(null, '', '#' + id);
    });
  });

  // Returns true when it took charge of the highlight, so the caller knows not to
  // immediately recompute from scroll position and undo it.
  function fromHash() {
    var h = decodeURIComponent((location.hash || '').slice(1));
    if (!h) return false;
    var el = openTarget(h);
    if (!el) return false;

    // Highlight FIRST. Scrolling is a nicety and scrollIntoView is not universally
    // present; if it were to throw here it must not take the highlight with it.
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('data-rail-target') === h) { setActive(links[i]); hashLock = true; break; }
    }
    if (typeof el.scrollIntoView === 'function') { el.scrollIntoView({ block: 'start' }); }
    return true;
  }

  window.addEventListener('scroll', function () {
    hashLock = false;    // the reader has taken over; scroll position rules again
    onScroll();
  }, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  // An opening/closing accordion changes offsets without any scroll happening.
  root.addEventListener('toggle', onScroll, true);

  // On a deep link the hash owns the highlight; running onScroll() here as well would
  // recompute from the pre-jump scroll position and clear it. Later scroll events take
  // over normally.
  if (!fromHash()) { onScroll(); }
  window.addEventListener('hashchange', fromHash);
})();
